import { useEffect, useRef, useState } from 'react';
import { useTranslations } from '@/i18n/translations';
import { Caption, Field, Stack, Textarea } from '@leanin/ui';
import { FormModal } from '../modals/FormModal';
import { ReasonRadioGroup } from './ReasonRadioGroup';
import { useErrorMessage } from '../../lib/error-message';
import {
  useCreateContentReport,
  type ReportReason,
  type ReportTargetKind,
} from '../../lib/mutations/moderation';

export type SubmitReportInput = {
  reason: ReportReason;
  reasonText: string;
};

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  targetKind: ReportTargetKind;
  /** Name of the reported subject (post author, circle name) — personalizes
   *  the dialog copy. Omit for the generic phrasing. */
  subjectName?: string;
  /** Fires once per successful submission, before the confirmation state
   *  shows. Lets flows that retire the reported thing (message requests)
   *  react when the dialog later closes. */
  onSubmitted?: () => void;
} & (
  | {
      /** Default path: file via POST /api/v1/moderation/reports. */
      targetId: string;
      submit?: never;
    }
  | {
      /** Custom path: the caller owns the report mutation (e.g. the
       *  message-request report endpoint, which also declines the request in
       *  the same transaction). The dialog still owns reason collection,
       *  validation, and the confirmation state. */
      targetId?: never;
      /** Rejects on failure; the dialog then shows `submitFailedCopy`
       *  (unless the rejection carries a cataloged API error code). */
      submit: (input: SubmitReportInput) => Promise<{ alreadyReported: boolean }>;
      submitFailedCopy: string;
    }
);

type ReasonOption = {
  value: ReportReason;
  /** Segment under `report.reasons.*` in the Moderation catalog. */
  key: 'spam' | 'harassment' | 'selfHarm' | 'illegal' | 'other';
};

const REASON_OPTIONS: ReadonlyArray<ReasonOption> = [
  { value: 'spam', key: 'spam' },
  { value: 'harassment', key: 'harassment' },
  { value: 'self_harm', key: 'selfHarm' },
  { value: 'illegal', key: 'illegal' },
  { value: 'other', key: 'other' },
];

export function ReportContentDialog(props: Props) {
  const { isOpen, onOpenChange, targetKind, subjectName, onSubmitted } = props;
  const t = useTranslations('Moderation');
  const describeError = useErrorMessage();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyReported, setAlreadyReported] = useState(false);
  const [customSubmitting, setCustomSubmitting] = useState(false);

  const reportMutation = useCreateContentReport();
  const submitting = reportMutation.isPending || customSubmitting;

  const reset = () => {
    setReason(null);
    setDetails('');
    setError(null);
    setSubmitted(false);
    setAlreadyReported(false);
    setCustomSubmitting(false);
    reportMutation.reset();
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(reset, 200);
  };

  // The dialog is mounted unconditionally by several parents, so its state
  // outlives a close. A fresh open must start from the form — without this,
  // a `submitted` that landed after the close-timer reset latched the
  // confirmation screen for every future target.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) reset();
    wasOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset is stable in behavior; keying on isOpen only
  }, [isOpen]);

  const onSuccess = (data: { alreadyReported: boolean }) => {
    setAlreadyReported(data.alreadyReported);
    setSubmitted(true);
    onSubmitted?.();
  };

  const onSubmit = () => {
    if (reason === null) return;
    setError(null);
    if (props.submit !== undefined) {
      setCustomSubmitting(true);
      props
        .submit({ reason, reasonText: details })
        .then(onSuccess)
        .catch((err: unknown) => {
          setError(describeError(err, props.submitFailedCopy));
        })
        .finally(() => setCustomSubmitting(false));
      return;
    }
    reportMutation.mutate(
      { targetKind, targetId: props.targetId, reason, reasonText: details },
      {
        onSuccess,
        onError: (err) => {
          setError(describeError(err, t('report.errors.submit')));
        },
      },
    );
  };

  const detailsRequired = reason === 'other';
  const detailsTooShort =
    detailsRequired && details.trim().length === 0;
  const submitDisabled = reason === null || detailsTooShort;

  if (submitted) {
    return (
      <FormModal
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) close();
        }}
        title={
          alreadyReported
            ? t('report.alreadyTitle')
            : t('report.submittedTitle')
        }
        description={
          alreadyReported
            ? t('report.alreadyBody')
            : t('report.submittedBody')
        }
        primaryLabel={t('report.done')}
        secondaryLabel={null}
        onSubmit={close}
      >
        <></>
      </FormModal>
    );
  }

  return (
    <FormModal
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) close();
      }}
      title={t(`report.${targetKind}.title`)}
      description={
        subjectName !== undefined
          ? t(`report.${targetKind}.descriptionNamed`, { subject: subjectName })
          : t(`report.${targetKind}.description`)
      }
      primaryLabel={t('report.submit')}
      primaryVariant="primary"
      primaryDisabled={submitDisabled}
      isSubmitting={submitting}
      error={error}
      onSubmit={onSubmit}
    >
      <Stack gap="md">
        <ReasonRadioGroup
          options={REASON_OPTIONS.map((opt) => ({
            value: opt.value,
            label: t(`report.reasons.${opt.key}.label`),
            description: t(`report.reasons.${opt.key}.description`),
          }))}
          value={reason}
          onChange={setReason}
          ariaLabel={t('report.reasonAria')}
        />
        <Field
          label={t('report.detailsLabel')}
          required={detailsRequired}
          description={
            detailsRequired
              ? t('report.detailsHelpRequired')
              : t('report.detailsHelpOptional')
          }
        >
          <Textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            rows={4}
            maxLength={2000}
            placeholder={t('report.detailsPlaceholder')}
          />
        </Field>
        <Caption>
          {t(`report.${targetKind}.caption`)}
        </Caption>
      </Stack>
    </FormModal>
  );
}
